import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Trophy, RotateCcw, Sparkles, Award, Check } from "lucide-react";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const C = {
  cardBg: "#111111",
  headerBg: "#0d0d0d",
  copper: "#dcaa2c",
  copperLight: "rgba(220,170,44,0.2)",
  textMuted: "#888",
};

/* "Test this card" — a quick 4-question quiz (measurement/brand/garnish/
   glassware) for a single cocktail, awards the same XP as Challenge Mode,
   and updates the card's mastery/glow state on completion. */
export function CardTestModal({ cocktail, onClose, onComplete }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [correct, setCorrect] = useState(0);
  const [wrong, setWrong] = useState(0);
  const [finished, setFinished] = useState(false);
  const [xpData, setXpData] = useState(null);
  const [startTime] = useState(Date.now());
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);

  // a11y: focus the dialog on open, restore focus to whatever triggered it
  // on close, close on Escape, and trap Tab navigation inside the modal
  // while it's open (previously keyboard users could tab/escape right past
  // it into the page behind).
  useEffect(() => {
    const previouslyFocused = document.activeElement;
    closeBtnRef.current?.focus();

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const focusable = dialogRef.current.querySelectorAll(
        'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      previouslyFocused?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.post(`${API}/challenge/card-test`, { cocktail_id: cocktail.id })
      .then(res => { if (active) setQuestions(res.data.questions); })
      .catch(err => { if (active) setError(err?.response?.data?.detail || "Failed to load questions"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [cocktail.id]);

  const q = questions[current];
  const total = questions.length;
  const pct = total > 0 ? Math.round((correct / total) * 100) : 0;

  const handleAnswer = (option) => {
    if (showFeedback) return;
    setSelected(option);
    setShowFeedback(true);
    if (option === q.correct_answer) setCorrect(c => c + 1);
    else setWrong(w => w + 1);
  };

  const handleNext = () => {
    if (current + 1 >= total) {
      const duration = Math.round((Date.now() - startTime) / 1000);
      axios.post(`${API}/xp/award-challenge`, {
        challenge_type: "card-test", correct, total, deck_name: cocktail.name,
        duration_seconds: duration, cocktail_id: cocktail.id,
      }).then(res => {
        setXpData(res.data);
        // Use the server's authoritative review-scheduling status (Phase 2)
        // instead of guessing client-side — a lower score on an already
        // "mastered" card must not locally flip it red before its real
        // review_due_at has passed.
        if (res.data.card_mastery) {
          onComplete?.(cocktail.id, res.data.card_mastery);
        }
      }).catch(() => {});
      setFinished(true);
      return;
    }
    setCurrent(c => c + 1);
    setSelected(null);
    setShowFeedback(false);
  };

  const retry = () => {
    setFinished(false); setCurrent(0); setSelected(null);
    setShowFeedback(false); setCorrect(0); setWrong(0); setXpData(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4" onClick={onClose} data-testid="card-test-modal">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="card-test-modal-title"
        className="relative w-full max-w-md rounded-xl border overflow-hidden"
        style={{ borderColor: C.copper, background: C.cardBg }}
        onClick={e => e.stopPropagation()}
      >
        <button ref={closeBtnRef} onClick={onClose} className="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/60" data-testid="card-test-close" aria-label="Close challenge card test">
          <X size={16} className="text-white" />
        </button>

        {loading && (
          <div className="p-10 text-center">
            <p style={{ color: C.textMuted }}>Loading questions...</p>
          </div>
        )}

        {!loading && error && (
          <div className="p-10 text-center space-y-3">
            <p className="text-red-400 text-sm" data-testid="card-test-error">{error}</p>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </div>
        )}

        {!loading && !error && !finished && q && (
          <>
            <div className="px-4 py-3 flex items-center justify-between" style={{ background: C.headerBg }}>
              <div className="flex items-center gap-2">
                <Sparkles size={14} style={{ color: C.copper }} />
                <span className="text-sm font-bold text-white" id="card-test-modal-title">{cocktail.name}</span>
              </div>
              <span className="text-xs" style={{ color: C.textMuted }}>{current + 1}/{total}</span>
            </div>
            <Progress value={((current + (showFeedback ? 1 : 0)) / total) * 100} className="h-1 bg-[#222] rounded-none" />
            <div className="p-4">
              <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: C.copper }}>{q.type}</p>
              <p className="text-white text-sm font-medium mb-4" style={{ fontFamily: "Georgia, serif" }} data-testid="card-test-question">{q.question_text}</p>
              <div className="space-y-2">
                {q.options.map((opt, i) => {
                  let bg = "rgba(255,255,255,0.05)", border = "#333", textColor = "#fff";
                  const isCorrectOpt = showFeedback && opt === q.correct_answer;
                  const isWrongSelected = showFeedback && opt === selected && opt !== q.correct_answer;
                  if (isCorrectOpt) { bg = "rgba(34,197,94,0.15)"; border = "#22c55e"; textColor = "#22c55e"; }
                  else if (isWrongSelected) { bg = "rgba(239,68,68,0.15)"; border = "#ef4444"; textColor = "#ef4444"; }
                  else if (opt === selected) { bg = C.copperLight; border = C.copper; }
                  return (
                    <button key={i} onClick={() => handleAnswer(opt)} disabled={showFeedback}
                      className="w-full text-left px-4 py-3 rounded-lg border text-sm transition-all flex items-center justify-between gap-2"
                      style={{ background: bg, borderColor: border, color: textColor }}
                      data-testid={`card-test-option-${i}`}
                    >
                      <span>{opt}</span>
                      {isCorrectOpt && <Check size={16} className="shrink-0" aria-label="Correct answer" />}
                      {isWrongSelected && <X size={16} className="shrink-0" aria-label="Your incorrect answer" />}
                    </button>
                  );
                })}
              </div>
              {showFeedback && (
                <Button onClick={handleNext} className="w-full font-bold mt-4" style={{ background: C.copper, color: "#000" }} data-testid="card-test-next">
                  {current + 1 >= total ? "See Result" : "Next"}
                </Button>
              )}
            </div>
          </>
        )}

        {!loading && !error && finished && (
          <div className="p-6 text-center space-y-4">
            <Trophy size={40} className="mx-auto" style={{ color: pct === 100 ? "#22c55e" : C.copper }} />
            <p className="text-3xl font-bold text-white" data-testid="card-test-score">{pct}%</p>
            <p className="text-sm" style={{ color: C.textMuted }}>{correct}/{total} correct</p>
            {pct === 100 && (
              <p className="text-sm font-bold text-green-400">
                Mastered! This card is now green until its next review{xpData?.card_mastery?.review_due_at ? ` (${new Date(xpData.card_mastery.review_due_at).toLocaleDateString()})` : ""}.
              </p>
            )}
            {pct < 100 && xpData?.card_mastery?.mastered && (
              <p className="text-sm font-semibold text-green-400/90">Still mastered — this card's review isn't due yet.</p>
            )}
            {xpData && (
              <div className="rounded-lg border p-3" style={{ borderColor: C.copperLight, background: "rgba(220,170,44,0.05)" }} data-testid="card-test-xp">
                <p className="text-lg font-bold" style={{ color: C.copper }}>+{xpData.xp_earned} XP</p>
                {xpData.new_badges?.length > 0 && xpData.new_badges.map((b, i) => (
                  <div key={i} className="flex items-center justify-center gap-2 text-sm font-bold text-[#C36A2F] mt-1">
                    <Award size={14} /> {b.name}
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={retry} data-testid="card-test-retry">
                <RotateCcw size={14} className="mr-2" /> Retry
              </Button>
              <Button className="flex-1" style={{ background: C.copper, color: "#000" }} onClick={onClose} data-testid="card-test-done">Done</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
